import React from 'react';
import type { IconProps } from '@tabler/icons-react-native';
import { StyleSheet, type StyleProp, type ViewStyle } from 'react-native';

import { Box, Text, useTheme } from 'app/shopify/theme';
import styles from 'app/shopify/tokens/styles.json';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

export type TagType = 'primary' | 'primaryNeutral' | 'secondary';
export type TagSize = 'default' | 'small';

export interface TagProps {
  label: string;
  type?: TagType;
  size?: TagSize;
  /** Primary Neutral has no inverted treatment in Figma and ignores this prop. */
  inverted?: boolean;
  /** Tabler icon elements receive the tag's size, color, and stroke weight. */
  leadingIcon?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

const LABEL_PADDING_HORIZONTAL_BY_SIZE: Record<TagSize, 'spacing.general.x1' | 'spacing.general.x0,5'> = {
  default: 'spacing.general.x1',
  small: 'spacing.general.x0,5',
};

const ROOT_PADDING_VERTICAL_BY_SIZE: Record<TagSize, 'spacing.general.x1' | 'spacing.general.x0,5'> = {
  default: 'spacing.general.x1',
  small: 'spacing.general.x0,5',
};

// The generated theme deliberately uses `normal` line heights. Tags need the
// source typography style's explicit line height to retain their compact layout.
const LABEL_LINE_HEIGHT_RATIO = Number.parseFloat(styles.typography.body['x-small'].$value.lineHeight) / 100;

function getTagColorKeys(type: TagType, inverted: boolean) {
  if (type === 'primary') {
    return {
      background: inverted ? 'surface.default' : 'surface.brand.strong',
      text: inverted ? 'text.brand' : 'text.inverse.primary',
    } as const;
  }

  if (type === 'primaryNeutral') {
    return { background: 'surface.secondary-neutral', text: 'text.secondary' } as const;
  }

  return {
    background: undefined,
    text: inverted ? 'text.inverse.primary' : 'text.secondary',
  } as const;
}

/** Static status or category label; tags are not interactive controls. */
export function Tag({
  label,
  type = 'primary',
  size = 'default',
  inverted = true,
  leadingIcon,
  style,
  testID,
}: TagProps) {
  const theme = useTheme();

  const hasBorder = type === 'secondary';
  const colorKeys = getTagColorKeys(type, inverted);
  const textColor = theme.colors[colorKeys.text];
  const lineHeight = Math.round(
    Number(theme.tokens.typography['typography.font-size.body.x-small']) * LABEL_LINE_HEIGHT_RATIO,
  );
  const iconSize = Number(theme.tokens.sizing[size === 'default' ? 'sizing.general.x4' : 'sizing.general.x3']);
  const icon = React.isValidElement<IconProps>(leadingIcon)
    ? React.cloneElement(leadingIcon, { size: iconSize, color: textColor, strokeWidth: ICON_STROKE_WIDTH })
    : leadingIcon;

  return (
    <Box
      accessible
      accessibilityRole="text"
      accessibilityLabel={label}
      testID={testID}
      alignSelf="flex-start"
      maxWidth="100%"
      flexShrink={1}
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      borderRadius="radius.full"
      paddingHorizontal="spacing.general.x1"
      paddingVertical={ROOT_PADDING_VERTICAL_BY_SIZE[size]}
      minHeight={size === 'default' ? Number(theme.tokens.sizing['sizing.general.x6']) : undefined}
      backgroundColor={colorKeys.background}
      style={style}
    >
      {/* Figma's inside stroke does not take space in the auto-layout. */}
      {hasBorder ? (
        <Box
          pointerEvents="none"
          borderRadius="radius.full"
          borderWidth={theme.borderWidths['border-width.subtle']}
          borderColor={inverted ? 'border.inverse.primary' : 'border.secondary'}
          style={StyleSheet.absoluteFill}
        />
      ) : null}
      {leadingIcon ? (
        <Box
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          aria-hidden
          width={iconSize}
          height={iconSize}
          flexShrink={0}
          alignItems="center"
          justifyContent="center"
        >
          {icon}
        </Box>
      ) : null}

      <Box
        alignItems="center"
        justifyContent="center"
        flexShrink={1}
        minWidth={0}
        paddingHorizontal={LABEL_PADDING_HORIZONTAL_BY_SIZE[size]}
      >
        <Text variant="bodyXSmall" color={colorKeys.text} lineHeight={lineHeight} textAlign="center">
          {label}
        </Text>
      </Box>
    </Box>
  );
}
